import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useStaffAuth } from './StaffAuthContext';
import { formatDate } from './tenantClock';
import './NotificationBell.css';

// The header's notification bell (issue #215): the signed-in staff
// member's own alerts, raised alongside each Sales Settings alert's email
// (backend/services/notificationService.js). The unread badge refreshes
// every minute and whenever the dropdown opens. Clicking an alert opens its
// record and marks it read; read alerts stay, greyed, for 30 days. While
// the tenant is still on the shared app password there's nobody to alert,
// so it shows the bell alone.
const POLL_MS = 60000;

function timeAgo(value) {
  const minutes = Math.floor((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return formatDate(value);
}

function NotificationBell() {
  const { currentUser } = useStaffAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [alerts, setAlerts] = useState(null);
  const containerRef = useRef(null);

  const loadCount = useCallback(() => axios.get('/api/notifications/unread-count')
    .then((res) => setUnread(res.data.data?.count || 0))
    .catch(() => {}), []);

  const loadAlerts = useCallback(() => axios.get('/api/notifications')
    .then((res) => {
      setAlerts(res.data.data?.alerts || []);
      setUnread(res.data.data?.unread || 0);
    })
    .catch(() => setAlerts((prev) => prev || [])), []);

  useEffect(() => {
    if (!currentUser) return undefined;
    loadCount();
    const timer = setInterval(loadCount, POLL_MS);
    return () => clearInterval(timer);
  }, [currentUser, loadCount]);

  useEffect(() => {
    if (!open) return undefined;
    loadAlerts();
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, loadAlerts]);

  if (!currentUser) {
    return (
      <span className="nav-btn module-nav-btn" style={{ display: 'flex', alignItems: 'center', cursor: 'default' }}>
        <Bell size={20} strokeWidth={1.5} />
      </span>
    );
  }

  const openAlert = (alert) => {
    setOpen(false);
    if (!alert.read) {
      setAlerts((prev) => prev.map((a) => (a.id === alert.id ? { ...a, read: true } : a)));
      setUnread((n) => Math.max(0, n - 1));
      axios.post(`/api/notifications/${alert.id}/read`).catch(() => {}).finally(loadCount);
    }
    navigate(alert.link);
  };

  const markAllRead = () => {
    setAlerts((prev) => (prev || []).map((a) => ({ ...a, read: true })));
    setUnread(0);
    axios.post('/api/notifications/read-all').catch(() => {}).finally(loadCount);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="nav-btn module-nav-btn notification-bell-btn"
        title={unread ? `${unread} unread alert${unread === 1 ? '' : 's'}` : 'Alerts'}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Bell size={20} strokeWidth={1.5} />
        {unread > 0 && <span className="notification-bell-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <span>Alerts</span>
            <button type="button" className="notification-mark-all" onClick={markAllRead} disabled={!unread} title="Mark all your alerts read">
              Mark all read
            </button>
          </div>
          <div className="notification-list">
            {alerts === null && <div className="notification-empty">Loading…</div>}
            {alerts && alerts.length === 0 && <div className="notification-empty">You have no alerts.</div>}
            {alerts && alerts.map((alert) => (
              <button
                key={alert.id}
                type="button"
                className={`notification-item${alert.read ? ' read' : ''}`}
                onClick={() => openAlert(alert)}
              >
                <span className="notification-item-meta">
                  <span>{alert.type}</span>
                  <span>{timeAgo(alert.created_at)}</span>
                </span>
                <span className="notification-item-title">{alert.title}</span>
                {alert.unassigned && <span className="notification-item-flag">No staff member set — set up Alerts</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
